/**
 * Helpers for the dashboard "My schedule" panel (PR 7a). Items come from
 * GET /users/me/schedule in the API's camelCase shape.
 */
import { ordinal } from '../league/leagueStats';
import { eventDayKey, eventZoneName, formatEventTime, formatViewerTime } from '../../utils/eventTime';

const DAY_MS = 86400000;

const dayNumber = (key) => Date.parse(`${key}T00:00:00Z`) / DAY_MS;

/**
 * "Today", "Tomorrow", else "Tue, Oct 27": the day at the venue (an 8 PM
 * Toronto game is on the Toronto day, wherever the viewer is).
 */
export const dayLabel = (startsAt, now = new Date(), timeZone) => {
  const diff = Math.round(dayNumber(eventDayKey(startsAt, timeZone)) - dayNumber(eventDayKey(now, timeZone)));
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return formatEventTime(startsAt, timeZone, { weekday: 'short', month: 'short', day: 'numeric' });
};

/** Live items first as "Live now", then one group per day, in the server's order. */
export const groupByDay = (items = [], now = new Date()) => {
  const groups = [];
  const live = items.filter((i) => i.live);
  if (live.length) groups.push({ key: 'live', label: 'Live now', items: live });
  for (const item of items.filter((i) => !i.live)) {
    const label = dayLabel(item.startsAt, now, item.timeZone);
    const last = groups[groups.length - 1];
    if (last && last.key !== 'live' && last.label === label) {
      last.items.push(item);
    } else {
      groups.push({ key: `day-${groups.length}`, label, items: [item] });
    }
  }
  return groups;
};

/** "7:00 PM" at the venue; the zone and the viewer's own time go in metaLine. */
export const timeLabel = (startsAt, timeZone) =>
  formatEventTime(startsAt, timeZone, { hour: 'numeric', minute: '2-digit', timeZoneName: undefined });

const ROLE_LABELS = { captain: 'Captain', referee: 'Ref', host: 'Host' };

/** The role badge, or null for a plain player. */
export const roleLabel = (role) => ROLE_LABELS[role] || null;

/**
 * "Gladstone 2026-27 Fall · Block & Roll · 2nd · 8-4" for a player,
 * "Gladstone 2026-27 Fall · Host · 6 teams" for the host (· Host when both).
 */
export const inProgressLine = (entry) => {
  const parts = [entry.name, entry.teamName];
  if (entry.position && entry.played > 0) {
    parts.push(ordinal(entry.position), `${entry.wins ?? 0}-${entry.losses ?? 0}`);
  }
  if (entry.host) {
    parts.push('Host');
    if (!entry.teamName && entry.entries != null) {
      parts.push(`${entry.entries} ${entry.kind === 'tournament' ? 'entries' : 'teams'}`);
    }
  }
  return parts.filter(Boolean).join(' · ');
};

/**
 * "Gladstone 2026-27 Fall · Gladstone HS · Boys Gym", then for a viewer in
 * another zone the venue's zone and their own time ("Eastern Time · 6:00 AM your time").
 */
export const metaLine = (item) => [
  item.competition, item.venue, item.court,
  !item.live && eventZoneName(item.startsAt, item.timeZone),
  !item.live && formatViewerTime(item.startsAt, item.timeZone),
].filter(Boolean).join(' · ');
